import { Card, Typography } from "@payping/web-components";
import React from "react";
import { useAsync } from "react-use";
import { AsyncState } from "react-use/lib/useAsync";
import { safeJoinPath } from "@payping/utils/string";
import { webConstants } from "@payping/utils/platformConstants";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { blogCardId } from "@/assets/googleTrackIds";

interface BlogPost {
  id: number;
  title: string;
  thumbnail: string;
  permalink: string;
  excerpt: string;
}

function useBlogPost(): AsyncState<BlogPost> {
  return useAsync(async () => {
    const response = await fetch(safeJoinPath(webConstants.STORAGE_URL, "documents/feed.json"));
    const latestPost: BlogPost = await response.json();
    return latestPost;
  }, []);
}

interface Props {
  loading: boolean;
  value?: BlogPost;
}

interface BlogCardI extends React.FC<Props> {
  useBlogPost: () => AsyncState<BlogPost>;
}

const BlogCard: BlogCardI = ({ loading, value }) => {
  const { t } = useAppTranslation();

  const renderContent = () => {
    if (loading) {
      return (
        <Card.Raw.ColMeta>
          <Typography.Text>{t("overview.blogCard.loading")}</Typography.Text>
        </Card.Raw.ColMeta>
      );
    }

    if (!value) {
      return (
        <Card.Raw.ColMeta>
          <Typography.Text>{t("overview.blogCard.error")}</Typography.Text>
        </Card.Raw.ColMeta>
      );
    }

    return (
      <Card.Raw.ColMeta containerStyle={{ paddingBottom: 0, paddingTop: 0 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Card.Raw.RowMeta containerStyle={{ paddingBottom: 0 }} childrenJustify="flex-start">
            <img
              src={value?.thumbnail}
              style={{ borderRadius: "1vh", height: "6vh", objectFit: "contain", width: "10vh" }}
            />
            <Typography.Paragraph
              ellipsis={{ rows: 2 }}
              style={{ fontSize: "2vh", fontWeight: 700, marginBottom: "0", padding: "0 8px" }}
            >
              {value?.title}
            </Typography.Paragraph>
          </Card.Raw.RowMeta>
          <Card.Raw.RowMeta containerStyle={{ paddingTop: "0.5vh" }}>
            <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ fontSize: "1.7vh", marginBottom: "0" }}>
              {value?.excerpt}
            </Typography.Paragraph>
          </Card.Raw.RowMeta>
        </div>
      </Card.Raw.ColMeta>
    );
  };

  const url = value?.permalink || safeJoinPath(webConstants.BLOG_URL);

  return (
    <a href={url} target="_blank" rel="noreferrer" style={{ color: "black" }} data-google-track={blogCardId}>
      <Card.Raw clickable>{renderContent()}</Card.Raw>
    </a>
  );
};

BlogCard.useBlogPost = useBlogPost;

export default BlogCard;
